import { RightPanelType } from "@kuika/kuika-cl-model";
import type { IScreenDesignContextState } from "../../../../../contexts/screen-design-context/screen-design-context";
import { getMcpChatPanelInstance } from "./mcp-chat-panel-ref";

/**
 * Is the Kuika AI chat currently on screen?
 *
 * "On screen" has two shapes. Docked into the right panel it means the AIChat tab is the active
 * one AND the panel is expanded — a collapsed panel with AIChat selected shows nothing. Floating
 * it is just the context flag. Callers that paint an active state must use this, not the flag
 * alone, or the trigger lights up for a chat nobody can see.
 */
export const isMcpChatPanelOpen = (sd: IScreenDesignContextState): boolean => {
  if (sd.chatDockedToPanel === "right") {
    const isRightPanelCollapsed: boolean = sd.isRightPanelCollapsed?.[sd.dashboardState] === true;
    return !isRightPanelCollapsed && sd.getActiveRightPanel() === RightPanelType.AIChat;
  }
  return sd.isMcpChatVisible === true;
};

/**
 * Reveal the AI chat, whichever surface currently owns it.
 *
 * The chat lives in one of two places and the caller can't know which: docked into the right
 * panel (`chatDockedToPanel === "right"`, where visibility is a right-panel tab switch) or
 * floating (where visibility is `sd.isMcpChatVisible`). Every entry point — the top-bar trigger,
 * the Issues "Fix with AI" flow — must go through here; a bare `setIsMcpChatPanelVisible(true)`
 * silently does nothing while docked.
 */
export const openMcpChatPanel = (sd: IScreenDesignContextState): void => {
  if (sd.chatDockedToPanel === "right") {
    // Un-collapse first: `setActiveRightPanelByClick` only records which tab is active (its own
    // un-collapse branch is commented out), so switching to AIChat inside a collapsed panel
    // would silently show nothing. `handleDockChat` expands for the same reason.
    const isRightPanelCollapsed: boolean = sd.isRightPanelCollapsed?.[sd.dashboardState] === true;
    if (isRightPanelCollapsed) {
      sd.setRightPanelCollapsed(false);
    }
    sd.setActiveRightPanelByClick(RightPanelType.AIChat);
    return;
  }
  getMcpChatPanelInstance()?.props.setIsMcpChatPanelVisible(true);
};

/**
 * Open the chat if it is hidden, dismiss it if it is showing — what the top-bar trigger does.
 *
 * Docked, "dismiss" hands the right panel back to Properties rather than collapsing it: the
 * panel is shared real estate, and collapsing would take the user's Properties view down with
 * the chat. That is also what undocking does (`DesignLeftMenu.handleUndockChat`).
 */
export const toggleMcpChatPanel = (sd: IScreenDesignContextState): void => {
  const isOpen: boolean = isMcpChatPanelOpen(sd);
  if (!isOpen) {
    openMcpChatPanel(sd);
    return;
  }
  if (sd.chatDockedToPanel === "right") {
    sd.setActiveRightPanelByClick(RightPanelType.Properties);
    return;
  }
  getMcpChatPanelInstance()?.props.setIsMcpChatPanelVisible(false);
};
